import React, { useEffect, useEffectEvent } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import first from 'lodash/first';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  LoadingIndicator,
  Typography,
} from '@cfa/react-components';

import { UnkeyedDocument } from '@/hooks/useGetStructuredBrowseData';

import { pluralize } from '@/utils/language';

import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';

import Constants from '@/constants';

import { ProcedureStep } from '../AddProcedurePopup';
import { PROCEDURE_STEPS } from '../constants';
import { PopupDocument } from '../types';

import DocumentCheckbox from './DocumentCheckbox';

import { BaseReferenceDto } from '@/_generated/api-clients/xp';
import {
  search,
  SearchData,
  SearchDocument as Document,
  searchQueryKey,
} from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';
import SubcategoryDocument from '@/routes/Explore/Operations/components/SubcategoryDocument';

interface SearchResultsProps {
  searchTermQuery: string;
  setPreviewDocument: (
    doc:
      | Document
      | BaseReferenceDto
      | UnkeyedDocument
      | Record<string, unknown>,
  ) => void;
  setActiveStep: (step: ProcedureStep) => void;
  misspellingSuggestion: string;
  setMisspellingSuggestion: (value: string) => void;
  noMoreResults: boolean;
  setNoMoreResults: (value: boolean) => void;
  onSearchTermSelect: (
    suggestion: string | null,
    spellingSuggestion?: string,
  ) => void;
  selectedDocuments: PopupDocument[];
  setSelectedDocuments: (documents: PopupDocument[]) => void;
  selectedCountryCode?: string;
  onlyAllowOneDoc?: boolean;
}

export const StepThreeSearchResults: React.FC<SearchResultsProps> = ({
  selectedCountryCode,
  searchTermQuery,
  setPreviewDocument,
  setActiveStep,
  misspellingSuggestion,
  setMisspellingSuggestion,
  onSearchTermSelect,
  selectedDocuments,
  setSelectedDocuments,
  onlyAllowOneDoc = false,
}) => {
  const onDocumentClick = (doc: Document) => {
    setPreviewDocument(doc);
    setActiveStep(PROCEDURE_STEPS.PREVIEW);
  };

  const { t } = useTranslation();

  const { language, country } = useAtomValue(userPrefsAtom);

  const searchParams: SearchData['query'] = {
    country: selectedCountryCode ?? country.id ?? '',
    language,
    query: searchTermQuery,
  };

  const {
    data: searchResponse,
    isFetching,
    isPending,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: searchQueryKey({
      query: searchParams,
    }),
    queryFn: ({ pageParam }) =>
      search({
        query: {
          pageNumber: pageParam,
          ...searchParams,
        },
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.data?.page &&
      allPages.reduce(
        (sum, page) => sum + (page.data?.results?.length ?? 0),
        0,
      ) < lastPage.data.totalResults
        ? lastPage.data.page + 1
        : undefined,
    enabled: !!searchTermQuery,
    select: data => {
      const latestPageData = data.pages.at(-1)?.data;
      return latestPageData
        ? {
            ...latestPageData,
            results: data.pages.flatMap(page => page.data?.results ?? []),
          }
        : undefined;
    },
  });

  const handleSetMisspellingSuggestion = useEffectEvent(
    (suggestion: string) => {
      setMisspellingSuggestion(suggestion);
    },
  );

  useEffect(() => {
    if (!searchResponse) {
      return;
    }
    handleSetMisspellingSuggestion(
      first(searchResponse.spellingSuggestions) ?? '',
    );
  }, [searchResponse]);

  if (error) {
    datadogRum.addError(error);
    return <GenericError />;
  }

  return (
    <>
      <LoadingOverlay
        isOpen={(isFetching || isPending) && !isFetchingNextPage}
      />
      {misspellingSuggestion && !isFetching && (
        <span
          data-testid="MisspellingSuggestionWrapper"
          className="text-grayscale-gray7 mb-4 font-normal"
        >
          {t('Search.numberOfResultsFor')}
          {': '}
          <button
            type="button"
            className="text-brand-navy! font-bold!"
            data-testid="MisspellingSuggestion"
            onClick={() => onSearchTermSelect(null, misspellingSuggestion)}
          >
            {misspellingSuggestion}
          </button>
        </span>
      )}
      {!!searchResponse && (
        <Typography className="text-left!" variant="body1">
          {` ${searchResponse.totalResults} ${t('Search.numberOfResultsFor')} `}
          {<strong>{searchTermQuery}</strong>}
        </Typography>
      )}
      <div className="list-results">
        {!searchResponse?.results.length ? (
          <div className="px-8 py-6">{t('Search.noResults')}</div>
        ) : (
          searchResponse.results
            .filter((document): document is Document & { id: string } => {
              if (!document.id) {
                datadogRum.addError(
                  new Error('Document missing ID in search results', {
                    cause: {
                      document,
                      searchTermQuery,
                      selectedCountryCode,
                      language,
                    },
                  }),
                );
                return false;
              }
              return true;
            })
            .map(document => {
              const newId = document.id;

              const newDocument = {
                id: newId,
                type: Constants.STEP_TYPES.DOCUMENT,
                reference: {
                  id: newId,
                },
                name: {
                  en: language === 'en' ? document.name : null,
                  es: language === 'es' ? document.name : null,
                },
              } as PopupDocument;
              return (
                <Card
                  className="animate-fade-in! my-4! flex! flex-row! items-center! justify-between! pr-4!"
                  key={newId + JSON.stringify(document.tags)}
                  title={document.name}
                >
                  <SubcategoryDocument
                    icon={
                      document.icon === undefined ||
                      document.icon === 'placeholder'
                        ? 'Chickfila'
                        : document.icon
                    }
                    id={newId}
                    name={document.name ?? ''}
                    onClick={() => onDocumentClick(document)}
                  />
                  <DocumentCheckbox
                    documentId={newId}
                    newDocument={newDocument}
                    selectedDocuments={selectedDocuments}
                    setSelectedDocuments={setSelectedDocuments}
                    onlyAllowOneDoc={onlyAllowOneDoc}
                  />
                </Card>
              );
            })
        )}
      </div>
      {hasNextPage && (
        <Button
          className="mx-auto! mt-6! mb-4! block!"
          color="secondary"
          data-testid="LoadMore"
          disabled={isFetching || isFetchingNextPage}
          onClick={() => !isFetchingNextPage && fetchNextPage()}
          size="lg"
          variant="filled"
        >
          <>
            {isFetching && (
              <LoadingIndicator
                className="-mt-1.5! mr-2.5!"
                disabled
                size="sm"
                variant="inline"
              />
            )}
            {t('Button.loadMore')}
          </>
        </Button>
      )}
      {!!searchResponse?.results.length && (
        <p className="text-variant-body1">
          {t('Search.showingResultsFooter', {
            numberOfResults: searchResponse.totalResults,
            resultsText: pluralize(
              searchResponse.results.length,
              t('Search.resultText'),
              t('Search.resultsText'),
            ),
            showingResults: searchResponse.results.length,
          })}
        </p>
      )}
    </>
  );
};

export default StepThreeSearchResults;
